"use client";

import { Card } from "@/components/ui/card";
import translations from "@/lib/translations.json";
import {
  Calendar,
  CreditCard,
  DollarSign,
  Handshake,
  Users,
  Wallet,
} from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useWebsiteLang } from "../context/website-lang-context";

type ProblemAfterItemKey =
  keyof (typeof translations)["en"]["website"]["problem"]["after"]["items"];

type ModuleItem = {
  icon: typeof Users;
  key: ProblemAfterItemKey;
  angle: number;
  href: string;
};

type ModuleVisualStyle = {
  bg: string;
  border: string;
  color: string;
  text: string;
};

const moduleVisualStyles: Record<ProblemAfterItemKey, ModuleVisualStyle> = {
  scheduling: {
    bg: "bg-orange-500/15",
    border: "border-orange-500/20",
    color: "from-orange-500 to-red-600",
    text: "text-orange-400",
  },
  payments: {
    bg: "bg-teal-500/20",
    border: "border-teal-500/20",
    color: "from-teal-500 to-cyan-600",
    text: "text-teal-400",
  },
  people: {
    bg: "bg-blue-500/15",
    border: "border-blue-500/20",
    color: "from-blue-500 to-indigo-600",
    text: "text-blue-400",
  },
  customers: {
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/20",
    color: "from-emerald-500 to-teal-600",
    text: "text-emerald-400",
  },
  invoicing: {
    bg: "bg-violet-500/15",
    border: "border-violet-500/20",
    color: "from-violet-500 to-purple-600",
    text: "text-violet-400",
  },
  banking: {
    bg: "bg-cyan-500/15",
    border: "border-cyan-500/20",
    color: "from-cyan-500 to-blue-600",
    text: "text-cyan-400",
  },
};

const modules: ModuleItem[] = [
  {
    icon: Wallet,
    key: "payments",
    angle: 0,
    href: "/#feature-payments",
  },
  {
    icon: Users,
    key: "people",
    angle: 72,
    href: "/#feature-people",
  },
  {
    icon: Handshake,
    key: "customers",
    angle: 144,
    href: "/#feature-patients",
  },

  {
    icon: DollarSign,
    key: "invoicing",
    angle: 216,
    href: "/#feature-invoicing",
  },
  {
    icon: CreditCard,
    key: "banking",
    angle: 288,
    href: "/#feature-banking",
  },
];

export default function ProblemAfter() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [explosionRadius, setExplosionRadius] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isExploded, setIsExploded] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.problem.after;

  const getTargetRadius = useCallback(() => {
    if (typeof window === "undefined") return 170;
    if (window.innerWidth < 640) return 95;
    if (window.innerWidth < 1024) return 135;
    return 170;
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isExploded) {
          setIsVisible(true);
          setTimeout(() => {
            setExplosionRadius(getTargetRadius());
            setIsExploded(true);
          }, 500);
          observer.disconnect();
        }
      },
      { threshold: 0 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [getTargetRadius, isExploded]);

  useEffect(() => {
    const handleResize = () => {
      if (isExploded) {
        setExplosionRadius(getTargetRadius());
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getTargetRadius, isExploded]);

  useEffect(() => {
    const updateContainerSize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    };

    updateContainerSize();

    if (typeof ResizeObserver === "undefined" || !containerRef.current) {
      window.addEventListener("resize", updateContainerSize);
      return () => window.removeEventListener("resize", updateContainerSize);
    }

    const observer = new ResizeObserver(updateContainerSize);
    observer.observe(containerRef.current);

    window.addEventListener("resize", updateContainerSize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateContainerSize);
    };
  }, []);

  const getOrbitPoint = (angle: number) => {
    const centerX = containerSize.width / 2;
    const centerY = containerSize.height / 2;
    const radius = explosionRadius;
    const radians = ((angle - 90) * Math.PI) / 180;

    return {
      x: centerX + radius * Math.cos(radians),
      y: centerY + radius * Math.sin(radians),
    };
  };

  const targetRadius = getTargetRadius();
  const orbitProgress =
    targetRadius > 0 ? Math.min(1, explosionRadius / targetRadius) : 0;
  const pentagonRotation = 10;
  const centerItem = t.items.scheduling;
  const centerItemStyle = moduleVisualStyles.scheduling;

  return (
    <div className="pb-6 pt-10 w-full relative overflow-hidden bg-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.05),transparent_70%)] pointer-events-none" />
      <div className="absolute left-1 top-1 sm:left-4 sm:top-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 backdrop-blur-sm font-medium text-xs">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        {/* With Orquesti - one connected system */}
        {t.badge}
      </div>
      <div
        ref={containerRef}
        className="relative w-full flex items-center justify-center h-[340px] sm:h-[380px] md:h-[460px] lg:h-[500px] lg:scale-[0.85] xl:scale-[0.95]"
      >
        {/* glow */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
          <div className="absolute bg-white/5 rounded-full animate-ping-custom-slow w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] md:w-[320px] md:h-[320px]" />
          <div className="absolute bg-white/5 rounded-full animate-ping-custom-fast w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] md:w-[280px] md:h-[280px]" />
        </div>

        {/* scheduling centered circle */}
        <div className="absolute z-10 flex items-center justify-center w-24 h-24 sm:w-36 sm:h-36 lg:w-44 lg:h-44">
          <Link href="/#feature-schedule" className="block w-full h-full">
            <Card
              className={`flex flex-col items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer backdrop-blur-md shadow-xl border rounded-full group w-full h-full ${centerItemStyle.bg} ${centerItemStyle.border} hover:border-[hsl(37,100%,54%)]`}
            >
              <div
                className={`flex items-center justify-center rounded-full bg-gradient-to-br ${centerItemStyle.color} shadow-lg transition-transform group-hover:scale-105 p-2.5 sm:p-3.5 gap-2`}
              >
                <Calendar className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 text-white" />
              </div>
              <div className="text-center flex flex-col items-center">
                <p
                  className={`text-center  px-2 transition-colors group-hover:text-[hsl(37,100%,54%)] text-[10px] sm:text-sm ${centerItemStyle.text}`}
                >
                  {centerItem.label}
                </p>
                <div className="h-0 overflow-hidden group-hover:h-auto group-hover:mt-1 transition-all duration-300 opacity-0 group-hover:opacity-100">
                  <span className="text-[hsl(37,100%,54%)] font-medium border-b border-[hsl(37,100%,54%)]/50 pb-0.5 opacity-80 text-[9px] sm:text-[10px]">
                    {t.readMore}
                  </span>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* satelites */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 2 }}
        >
          {containerSize.width > 0 &&
            containerSize.height > 0 &&
            (() => {
              const orbitPoints = modules.map((satellite) =>
                getOrbitPoint(satellite.angle),
              );

              const closedPoints = [...orbitPoints, orbitPoints[0]];

              const points = closedPoints
                .map((satellite) => {
                  return `${satellite.x},${satellite.y}`;
                })
                .join(" ");

              const pathLength = orbitPoints.reduce(
                (total, point, pointIndex) => {
                  const nextPoint =
                    orbitPoints[(pointIndex + 1) % orbitPoints.length];
                  return (
                    total +
                    Math.hypot(nextPoint.x - point.x, nextPoint.y - point.y)
                  );
                },
                0,
              );

              return (
                <div
                  className="absolute inset-0"
                  style={{
                    zIndex: 1,
                    overflow: "visible",
                    transformOrigin: "center center",
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      animation: "pentagon-rotate 60s linear infinite",
                      transformOrigin: "center center",
                    }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        animation: "pentagon-breathe 6s ease-in-out infinite",
                        transformOrigin: "center center",
                      }}
                    >
                      <svg
                        className="absolute inset-0 h-full w-full"
                        viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
                        preserveAspectRatio="none"
                      >
                        <defs>
                          <style>{`
                        .neighbor-connector-line {
                          stroke: rgba(255,255,255,0.20);
                        }
                      `}</style>
                        </defs>
                        <polygon
                          points={points}
                          fill="none"
                          className="neighbor-connector-line"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeDasharray={Math.max(0.001, pathLength)}
                          strokeDashoffset={
                            Math.max(0.001, pathLength) * (1 - orbitProgress)
                          }
                          style={{
                            opacity: orbitProgress,
                            transition:
                              "stroke-dashoffset 3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease-out 150ms",
                          }}
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })()}
        </div>

        {modules.map((module, index) => {
          const item = t.items[module.key];
          const itemStyle = moduleVisualStyles[module.key];
          return (
            <div key={module.key} className="absolute inset-0">
              {/* connecting lines */}
              <div
                className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-700 ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  animation: `orbit-rotation-${index} 60s linear infinite`,
                }}
              >
                <div
                  className="absolute left-1/2 top-1/2 h-[2px] origin-left -translate-y-1/2 rounded-full"
                  style={{
                    width: `${Math.max(0, explosionRadius)}px`,
                    background: "rgba(255,255,255,0.20)",
                    opacity: isExploded ? 1 : 0,
                    transform: `scaleX(${isExploded ? 1 : 0})`,
                    transformOrigin: "left center",
                    transition:
                      "transform 3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s ease-out 150ms",
                  }}
                />
              </div>

              <div
                className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-700 ${
                  isVisible ? "opacity-100" : "opacity-0"
                }`}
                style={{
                  animation: `orbit-rotation-${index} 60s linear infinite`,
                  zIndex: 20,
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    transform: `translateX(${explosionRadius}px) translateZ(0)`,
                    transition: "transform 3s cubic-bezier(0.16, 1, 0.3, 1)",
                    backfaceVisibility: "hidden",
                  }}
                >
                  <div className="animate-breathe">
                    <div
                      style={{
                        animation: `counter-rotation-${index} 60s linear infinite`,
                        transformOrigin: "center center",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      <Link
                        href={module.href}
                        className="pointer-events-auto block"
                      >
                        <Card
                          className={`flex flex-col items-center justify-center hover:scale-110 transition-all duration-300 cursor-pointer backdrop-blur-md shadow-xl border rounded-full group w-[80px] h-[80px] sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 ${itemStyle.bg} ${itemStyle.border} hover:border-[hsl(37,100%,54%)]`}
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "translateZ(0)",
                          }}
                        >
                          <div className="flex flex-col items-center gap-2 sm:gap-3 justify-center">
                            <div
                              className={`flex items-center justify-center rounded-full bg-gradient-to-br ${itemStyle.color} shadow-lg transition-transform group-hover:scale-105 p-2 sm:p-2.5`}
                            >
                              <module.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-5 md:h-5 text-white" />
                            </div>
                            <div className="flex flex-col items-center">
                              <p
                                className={`text-center px-2 transition-colors group-hover:text-[hsl(37,100%,54%)] text-[10px] sm:text-sm ${itemStyle.text}`}
                              >
                                {item.label}
                              </p>
                              <div className="h-0 overflow-hidden group-hover:h-auto group-hover:mt-1 transition-all duration-300 opacity-0 group-hover:opacity-100">
                                <span className="text-[hsl(37,100%,54%)] font-medium border-b border-[hsl(37,100%,54%)]/50 pb-0.5 opacity-80 text-[9px] sm:text-[10px]">
                                  {t.readMore}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* bottom data tag */}
      <div
        className="absolute bottom-1 right-1 sm:bottom-3 sm:right-2  z-20 transition-all duration-700 delay-700"
        style={{ opacity: isExploded ? 1 : 0 }}
      >
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {t.footer}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        ${modules
          .map(
            (module, index) => `
          @keyframes orbit-rotation-${index} {
            from { transform: rotate(${module.angle}deg); }
            to { transform: rotate(${module.angle + 360}deg); }
          }
          @keyframes counter-rotation-${index} {
            from { transform: rotate(-${module.angle}deg); }
            to { transform: rotate(-${module.angle + 360}deg); }
          }
        `,
          )
          .join("")}

        @keyframes breathe {
          0%, 100% { transform: translateX(0px); }
          50% { transform: translateX(20px); }
        }

        @keyframes pentagon-breathe {
          0%, 100% { transform: rotate(${pentagonRotation}deg) scale(0.9); }
          50% { transform: rotate(${pentagonRotation}deg) scale(1.06); }
        }

        @keyframes pentagon-rotate {
          from { transform: rotate(${pentagonRotation}deg); }
          to { transform: rotate(${pentagonRotation + 360}deg); }
        }

        .animate-breathe {
          animation: breathe 6s ease-in-out infinite;
        }

        @keyframes ping-custom {
          0% { transform: scale(0.8); opacity: 0.6; }
          75%, 100% { transform: scale(2.2); opacity: 0; }
        }

        .animate-ping-custom-slow {
          animation: ping-custom 6s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        .animate-ping-custom-fast {
          animation: ping-custom 4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `,
        }}
      />
    </div>
  );
}
